import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Swords, RefreshCw } from 'lucide-react';
import Navigation from '@/components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WnbaMatchups = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/wnba/matchups`);
      setGames(res.data.games || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchData(); }, []);

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
                <Swords className="h-8 w-8 text-orange-400" />
                WNBA Scores & Matchups
              </h1>
              <p className="text-muted-foreground mt-1">Spreads, totals, consensus, and team form for every game</p>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-[#1e3a5f] text-white hover:bg-[#112240]" disabled={refreshing} data-testid="refresh-wnba-matchups-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-44 skeleton rounded-lg" />)}</div>
          ) : games.length === 0 ? (
            <Card className="bg-[#0f1e36] border-[#1e3a5f]"><CardContent className="p-12 text-center text-muted-foreground">No WNBA games on the schedule right now.</CardContent></Card>
          ) : (
            <div className="space-y-4">
              {games.map((g, i) => (
                <Card key={i} className="bg-[#0f1e36] border-[#1e3a5f]" data-testid={`wnba-game-${i}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                      <div>
                        <div className="font-heading text-xl font-bold text-white uppercase">
                          {g.away} {g.away_nick} @ {g.home} {g.home_nick}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">{g.date} {g.time && `• ${g.time}`}</div>
                      </div>
                      {g.is_final && <Badge variant="outline" className="border-brand-lime text-brand-lime">Final</Badge>}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      <div className="bg-[#0a1628] rounded p-3 text-center">
                        <div className="text-[10px] text-muted-foreground uppercase">{g.away_abbr} Spread</div>
                        <div className="font-mono font-bold text-brand-cyan">{g.away_spread ?? '—'}</div>
                      </div>
                      <div className="bg-[#0a1628] rounded p-3 text-center">
                        <div className="text-[10px] text-muted-foreground uppercase">{g.home_abbr} Spread</div>
                        <div className="font-mono font-bold text-brand-cyan">{g.home_spread ?? '—'}</div>
                      </div>
                      <div className="bg-[#0a1628] rounded p-3 text-center">
                        <div className="text-[10px] text-muted-foreground uppercase">Total (O/U)</div>
                        <div className="font-mono font-bold text-white">{g.total ?? '—'}</div>
                      </div>
                      <div className="bg-[#0a1628] rounded p-3 text-center">
                        <div className="text-[10px] text-muted-foreground uppercase">Consensus</div>
                        <div className="font-mono font-bold text-brand-lime">{g.consensus_away_pct ?? '—'}% / {g.consensus_home_pct ?? '—'}%</div>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-muted-foreground text-[10px] uppercase border-b border-[#1e3a5f]">
                            <th className="text-left py-1.5">Team</th>
                            <th className="text-center py-1.5">W/L</th>
                            <th className="text-center py-1.5">ATS</th>
                            <th className="text-center py-1.5">Last 10</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-[#1e3a5f]/40">
                            <td className="py-1.5 text-white font-medium">{g.away_abbr} {g.away_nick}</td>
                            <td className="text-center font-mono text-white">{g.away_record ?? '—'}</td>
                            <td className="text-center font-mono text-muted-foreground">{g.away_ats ?? '—'}</td>
                            <td className="text-center font-mono text-white">{g.away_l10 ?? '—'}</td>
                          </tr>
                          <tr>
                            <td className="py-1.5 text-white font-medium">{g.home_abbr} {g.home_nick}</td>
                            <td className="text-center font-mono text-white">{g.home_record ?? '—'}</td>
                            <td className="text-center font-mono text-muted-foreground">{g.home_ats ?? '—'}</td>
                            <td className="text-center font-mono text-white">{g.home_l10 ?? '—'}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default WnbaMatchups;
